import type { ComponentProps } from 'react'
import { useCallback, useEffect, useState } from 'react'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import type { OfferListSortOption } from 'constants/offerListFilters'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { CruiseSearchFilterKeys } from 'constants/cruise'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router'
import { useAppDispatch } from 'hooks/reduxHooks'
import CruiseSearchPriceChip from './CruiseSearchPriceChip'
import CruiseSearchShipChip from './CruiseSearchShipChip'
import CruiseSearchPromotionChip
  from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSearchMobileChips/CruiseSearchPromotionChip'
import CruiseSearchCruiseTypeChip
  from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSearchMobileChips/CruiseSearchCruiseTypeChip'
import useCruiseFacetFiltersParams from 'hooks/useCruiseFacetFiltersParams'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

export const HeaderContainer = styled.div`
  position: sticky;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  width: 100%;
  top: 0;

  &.lifted {
    box-shadow: ${(props) => props.theme.shadow.flat.small};
  }
`

export interface Form {
  minPrice?: number | undefined
  maxPrice?: number | undefined
  cabinTypes?: string | undefined
  cruiseShips?: Array<string> | undefined
  promotions?: Array<string> | undefined
  isSpecialOffers?: string | undefined
  isLuxExclusivePromotion?: string | undefined
  isLuxPlus?: string | undefined
  cruiseTypes?: Array<string> | undefined
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>

interface Props extends InheritedOfferSortControlProps {
  listFilters: App.OfferListFilters
  sortOptions: Array<OfferListSortOption>
  onShowFilters: () => Promise<void>
  drawerMode?: boolean
}

function CruiseSearchChips(props: Props) {
  const { sortOptions, listFilters, drawerMode, onSortChange } = props
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const isMobile = useScreenSizeOnly('mobile')
  const showSortDropdown = !isMobile
  const { facetParams, mappedFilters, queryParamsFilters: filters } = useCruiseFacetFiltersParams({ filters: listFilters })

  const [facetsV2, fetching] = useCruiseSearchFacets(facetParams)
  const [filterForm, setFilterForm] = useState({
    minPrice: mappedFilters.price?.min,
    maxPrice: mappedFilters.price?.max,
    cabinTypes: mappedFilters.cabinTypes,
    cruiseShips: mappedFilters.cruiseShips,
    promotions: mappedFilters.promotions,
    isSpecialOffers: filters.isSpecialOffers?.size! ? Array.from(filters.isSpecialOffers)[0] : undefined,
    isLuxExclusivePromotion: filters.isLuxExclusivePromotion?.size! ? Array.from(filters.isLuxExclusivePromotion)[0] : undefined,
    isLuxPlus: filters.isLuxPlus?.size! ? Array.from(filters.isLuxPlus)[0] : undefined,
    cruiseTypes: mappedFilters.cruiseTypes,
  } as Form)

  const onChangeFilterForm = useCallback((filterForm: Form) => {
    setFilterForm(filterForm)

    if (!filterForm) return

    if (filterForm && Object.keys(filterForm).length > 0) {
      const urlValues = Object.entries({ ...filterForm }).map(([key, value]) => ({ paramName: key, paramValue: value }))
      let nextSearch = deleteSearchParams(search, ...CruiseSearchFilterKeys)
      nextSearch = setManySearchParamValues(nextSearch, urlValues)
      dispatch(replace({ search: nextSearch }))
    } else {
      dispatch(replace({ search: undefined }))
    }
  }, [dispatch, search])

  // set initial filter form
  useEffect(() => {
    setFilterForm({
      minPrice: mappedFilters.price?.min,
      maxPrice: mappedFilters.price?.max,
      cabinTypes: mappedFilters.cabinTypes,
      cruiseShips: mappedFilters.cruiseShips,
      promotions: mappedFilters.promotions,
      isSpecialOffers: filters.isSpecialOffers?.size! ? Array.from(filters.isSpecialOffers)[0] : undefined,
      isLuxExclusivePromotion: filters.isLuxExclusivePromotion?.size! ? Array.from(filters.isLuxExclusivePromotion)[0] : undefined,
      isLuxPlus: filters.isLuxPlus?.size! ? Array.from(filters.isLuxPlus)[0] : undefined,
      cruiseTypes: mappedFilters.cruiseTypes,
    } as Form)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>
    <OfferSortControl
      sortOptions={sortOptions}
      roundedButton
      showSelectedHighlight
      showDropdown={showSortDropdown}
      onSortChange={onSortChange}
    />
    <>
      <CruiseSearchPriceChip
        drawerMode={drawerMode}
        facets={facetsV2}
        filters={filters}
        listFilters={{
          minPrice: listFilters.minPrice,
          maxPrice: listFilters.maxPrice,
        }}
        filterForm={filterForm}
        setFilterForm={onChangeFilterForm}
        fetching={fetching}
      />
      <CruiseSearchShipChip
        drawerMode={drawerMode}
        facets={facetsV2}
        filters={filters}
        filterForm={filterForm}
        setFilterForm={onChangeFilterForm}
        fetching={fetching}
      />
      <CruiseSearchPromotionChip
        drawerMode={drawerMode}
        facets={facetsV2}
        filters={filters}
        filterForm={filterForm}
        setFilterForm={onChangeFilterForm}
        fetching={fetching}
      />
      <CruiseSearchCruiseTypeChip
        drawerMode={drawerMode}
        facets={facetsV2}
        filters={filters}
        filterForm={filterForm}
        setFilterForm={onChangeFilterForm}
        fetching={fetching}
      />
    </>
  </>
}

export default CruiseSearchChips
